import { JSX } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { useAnimation, UseAnimationConfig } from '../hooks/UNSAFE_useAnimation';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { CollapsibleStyles } from './themes/CollapsibleStyles.css';

import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { CollapsibleRedwoodTheme } from './themes/redwood/CollapsibleTheme';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'children'>;

type ContentProps = IntrinsicProps & {
  id: string;
  isExpanded: boolean;
  onTransitionEnd: () => void;
};

/**
 * The 'collapsing' and 'expanding' states are related to the animation. The 'unmounted' state
 * is used for conditional rendering. In this state, the Collapsible content is completely removed
 * from the DOM. The 'unmounted' state is set either as an initial state or when collapsing
 * animation ends.
 */
type Status = 'collapsing' | 'expanding' | 'unmounted';

export const CollapsibleContent = ({ children, id, isExpanded, onTransitionEnd }: ContentProps) => {
  const [state, setState] = useState<Status>(isExpanded ? 'expanding' : 'unmounted');

  const { styles } = useComponentTheme<never, CollapsibleStyles>(CollapsibleRedwoodTheme);

  /**
   * 1. On initial render, Preact sets style.maxHeight field to 0 or 'none'.
   * 2. During expanding animation, useAnimation hook uses WAAPI to animate the maxHeight out
   *    to the expanded value.
   * 3. On subsequent re-renders, Preact does never updates the style.maxHeight value
   *    because even though we are telling Preact that maxHeight should be 0 or 'none', Preact
   *    considers the maxHeight value is 0 or 'none' as it is not aware that we changed this value
   *    in step 2. So from Preact's perspective there is no difference.
   */
  const contentInitialStyle = useRef<JSX.CSSProperties>({
    overflowY: 'hidden',
    maxHeight: isExpanded ? 'none' : '0'
  });

  useEffect(() => {
    // Ignore state update on initial render
    if (state === 'unmounted' && !isExpanded) {
      return;
    }
    setState(isExpanded ? 'expanding' : 'collapsing');
  }, [isExpanded, state]);

  const { nodeRef } = useAnimation<Status, HTMLDivElement>(state, {
    animationStates,
    onAnimationEnd: ({ animationState }) => {
      if (animationState === 'collapsing') {
        setState('unmounted');
      }
      onTransitionEnd?.();
    }
  });

  return (
    <div
      className={styles.contentChildrenStyle}
      ref={nodeRef}
      id={id}
      tabIndex={-1}
      style={contentInitialStyle.current}
      aria-hidden={!isExpanded || undefined}>
      {state !== 'unmounted' && children}
    </div>
  );
};

/**
 * The transition from collapsed to expanded works as follows:
 * 1. When the component is initially rendered in the collapsed state, we
 *    explicitly set maxHeight and overflowY from "to" configuration.
 * 2. When the expanded prop changes from false to true, we first mount the content children.
 * 3. Once this is mounted we trigger an animation updating animationState value of useAnimationHook.
 * 4. Configuration is represented inside hook. Passed config depends if previous animation was completed or not.
 *    We could check that based on node.style.maxHeight.
 *
 * The transition from expanded to collapsed is similar:
 * 1. We trigger an animation updating animationState value of useAnimationHook.
 * 2. Once animation is completed, with the help of end config,we set max-Height as 'none'.
 *    This will allow us to keep adding content.
 * 3. Once this is done, onAnimationEnd is called(as 'from collapsed to expanded"). Here, content children are unmouunted.
 */
const animationStates: UseAnimationConfig<Status, HTMLDivElement>['animationStates'] = {
  expanding: (node) => ({
    to: {
      maxHeight: `${node.scrollHeight}px`
    },
    options: {
      duration: 400
    },
    end: {
      maxHeight: 'none'
    }
  }),
  collapsing: (node) => ({
    ...(node.style.maxHeight === 'none' && {
      from: {
        maxHeight: `${node.scrollHeight}px`
      }
    }),
    to: {
      maxHeight: '0'
    },
    options: {
      duration: 400
    }
  })
};
